/**
 * Where the person is, for Lykke: the page and the object on it (a campaign, a deck, a draft, an order).
 *
 * Every page has a context without doing anything: the URL says the page (guide/pageMap.ts entryFor) and the ids in
 * it (campaign, creator, list). A page that knows more sets it with `usePageContext`: the object's name ("Spring
 * Home"), the deck or draft on screen, a short line of what is on screen. Lykke reads it at each ask (`here`), so
 * she knows where you are and what "this" means, on every page and in both layouts.
 *
 *   usePageContext({ page: 'reports', label: 'Deck studio', objects: { deckId }, title: deck.title })
 *
 * Values are read when Lykke is asked (kept in a ref), so a page may pass fresh values on every render.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { parseAppPath } from '@/routes';
import { entryFor } from '../guide/pageMap';

export interface PageObjects {
  campaignId?: string;
  deckId?: string;
  draftId?: string;
  orderId?: string;
  creatorId?: string;
  listId?: string;
  dashboardId?: string;
  mediaId?: string;
  [k: string]: string | undefined;
}

export interface PageContextValue {
  /** The page map id (guide/pageMap.ts), e.g. 'campaign-briefsBeats', 'reports'. */
  page?: string;
  /** The page in words, e.g. 'Campaign brief'. */
  label?: string;
  /** The object's name, e.g. the campaign or deck title. */
  title?: string;
  objects?: PageObjects;
  /** One short line of what is on screen (no money: costs never go to Lykke). */
  summary?: string;
}

/** What Lykke gets each turn: GuideHere on the backend ({ page, label, path }) plus the objects for the shell. */
export interface Here { page: string; label: string; path: string; objects: PageObjects; title?: string }

interface Registry { set: (key: symbol, v: PageContextValue | null) => void; here: () => Here }
const Ctx = createContext<Registry | null>(null);

/** The id-ish values of a URL (the router's own parser): campaign, creator, list, content. */
function routeObjects(pathname: string, search: string): PageObjects {
  const p = parseAppPath(pathname);
  const q = new URLSearchParams(search);
  const o: PageObjects = {};
  if (p.campaignId) o.campaignId = p.campaignId;
  if (p.creatorId) o.creatorId = p.creatorId;
  if (p.listId) o.listId = p.listId;
  if (p.contentHubMediaId) o.mediaId = p.contentHubMediaId;
  const edit = q.get('edit'); if (edit && /^[A-Za-z0-9_-]{3,64}$/.test(edit)) o.deckId = edit;
  const order = q.get('order'); if (order && /^[A-Za-z0-9_-]{3,64}$/.test(order)) o.orderId = order;
  const draft = q.get('draft_product_id'); if (draft && /^[A-Za-z0-9_-]{3,64}$/.test(draft)) o.draftId = draft;
  return o;
}

/** The label Lykke reads: "Campaign brief: Spring Home (campaign X1y2-cpg)". Ids stay in brackets so tools get them. */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function hereLabel(base: string, title: string | undefined, objects: PageObjects): string {
  const ids = Object.entries(objects).filter(([, v]) => !!v).map(([k, v]) => `${k.replace(/Id$/, '')} ${v}`);
  return `${base}${title ? `: ${title}` : ''}${ids.length ? ` (${ids.join(', ')})` : ''}`.slice(0, 80);
}

export function PageContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const loc = useRef(location); loc.current = location;
  const regs = useRef(new Map<symbol, PageContextValue>());
  const order = useRef<symbol[]>([]);

  const set = useCallback((key: symbol, v: PageContextValue | null) => {
    if (v) { regs.current.set(key, v); if (!order.current.includes(key)) order.current.push(key); }
    else { regs.current.delete(key); order.current = order.current.filter((k) => k !== key); }
  }, []);

  const here = useCallback((): Here => {
    const l = loc.current;
    const p = parseAppPath(l.pathname);
    const entry = entryFor(l.pathname, p.currentPage, p.subTab);
    // The latest registration wins field by field over the URL (a page knows its own object best).
    const latest = order.current.map((k) => regs.current.get(k)).filter(Boolean) as PageContextValue[];
    const merged = latest.reduce<PageContextValue>((acc, v) => ({ ...acc, ...v, objects: { ...acc.objects, ...v.objects } }), {});
    const objects: PageObjects = { ...routeObjects(l.pathname, l.search), ...(merged.objects || {}) };
    for (const k of Object.keys(objects)) if (!objects[k]) delete objects[k];
    const base = merged.label || entry?.label || p.currentPage;
    return {
      page: merged.page || entry?.id || p.currentPage,
      label: hereLabel(base, merged.title, objects),
      path: `${l.pathname}${l.search}`.slice(0, 300),
      objects,
      title: merged.title,
    };
  }, []);

  const value = useMemo(() => ({ set, here }), [set, here]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** A page says what it is and what is on it. Cleared when the page unmounts. */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function usePageContext(v: PageContextValue | null | undefined): void {
  const reg = useContext(Ctx);
  const key = useRef(Symbol('page'));
  const val = useRef(v); val.current = v;
  // Fresh values on every render (cheap: a Map write), removed on unmount.
  if (reg && v) reg.set(key.current, v);
  useEffect(() => {
    const k = key.current;
    if (reg && val.current) reg.set(k, val.current);
    return () => reg?.set(k, null);
  }, [reg]);
}

/** Read where the person is right now (call it at the moment of an ask). */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useHere(): () => Here | null {
  const reg = useContext(Ctx);
  return useCallback(() => (reg ? reg.here() : null), [reg]);
}
